import { useMemo, useState } from 'react';
import { useModels, type ModelInfo } from '@/hooks/useContent';

const IMG_COST_MICRO_USD = 260_000; // DALL-E 3 1792×1024 standard, matches image-pricing.ts
const DEFAULT_WORDS = 1000;

/**
 * Shows how many articles fit into the given balance (µUSD).
 * User picks the text model; rows update to that model's per-word cost.
 */
export function PackageUsageEstimate({ balanceMicroUsd }: { balanceMicroUsd: number }) {
  const { data } = useModels();
  const [modelId, setModelId] = useState('gpt-4o-mini');

  const selected: ModelInfo | null = useMemo(
    () => data?.models.find((m) => m.id === modelId) ?? null,
    [data, modelId],
  );

  if (balanceMicroUsd <= 0) return null;

  const perWord = selected?.credits_per_word ?? 3;
  const textPerArticle = perWord * DEFAULT_WORDS;
  const withFeatured = textPerArticle + IMG_COST_MICRO_USD;
  const withFeaturedPlus3Body = textPerArticle + IMG_COST_MICRO_USD * 4;

  const rows = [
    { label: 'Sadece metin', value: Math.floor(balanceMicroUsd / textPerArticle) },
    { label: '+ 1 öne çıkan görsel', value: Math.floor(balanceMicroUsd / withFeatured) },
    { label: '+ 1 featured + 3 body görsel', value: Math.floor(balanceMicroUsd / withFeaturedPlus3Body) },
  ];

  // Featured models + some cheap/premium picks, grouped by family for clarity.
  const groups = useMemo(() => {
    if (!data) return [];
    const byFamily: Record<string, ModelInfo[]> = {};
    for (const m of data.models) {
      (byFamily[m.family] = byFamily[m.family] ?? []).push(m);
    }
    return data.families.map((f) => ({
      id: f.id,
      label: f.label,
      models: byFamily[f.id] ?? [],
    }));
  }, [data]);

  return (
    <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-foreground">Üretilebilir yazı sayısı (1000 kelime):</span>
        <select
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          className="h-7 rounded border border-input bg-background px-2 text-xs max-w-[180px]"
        >
          {groups.map((g) => (
            <optgroup key={g.id} label={g.label}>
              {g.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      {rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between">
          <span className="text-muted-foreground">{r.label}</span>
          <span className="font-mono font-medium">{r.value.toLocaleString('tr-TR')} yazı</span>
        </div>
      ))}
      {selected && (
        <div className="text-[10px] text-muted-foreground border-t pt-1.5">
          Seçili model: <span className="font-mono">{selected.label}</span> · {perWord} µUSD/kelime · Görsel: 1 resim =
          ${(IMG_COST_MICRO_USD / 1_000_000).toFixed(2)}
        </div>
      )}
    </div>
  );
}
